import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LandingPage from 'pages/LandingPage';
import LoginPage from 'pages/auth/LoginPage';
import AcceptInvitePage from 'pages/auth/AcceptInvitePage';
import ConsolePage from 'pages/console/ConsolePage';
import DashboardPage from 'pages/console/DashboardPage';
import ReportsPage from 'pages/console/reports/ReportsPage';
import ConnectorsPage from 'pages/console/connectors/ConnectorsPage';
import NewConnectionPage from 'pages/console/connectors/NewConnectionPage';
import BigQuerySourceWizardPage from 'pages/console/connectors/BigQuerySourceWizardPage';
import AppMetricaSourceWizardPage from 'pages/console/connectors/AppMetricaSourceWizardPage';
import DirectSourceWizardPage from 'pages/console/connectors/DirectSourceWizardPage';
import FileSourcesWizardPage from 'pages/console/connectors/FileSourcesWizardPage';
import ConnectionDetailPage from 'pages/console/connectors/ConnectionDetailPage';
import ConnectedAccountsPage from 'pages/console/connectors/ConnectedAccountsPage';
import PipelinesPage from 'pages/console/pipelines/PipelinesPage';
import NewPipelinePage from 'pages/console/pipelines/NewPipelinePage';
import PipelineDetailPage from 'pages/console/pipelines/PipelineDetailPage';
import LegacyWarehouseRedirect from 'pages/console/warehouse/LegacyWarehouseRedirect';
import NewWarehouseServicePage from 'pages/console/warehouse/NewWarehouseServicePage';
import WarehouseServiceDetailPage from 'pages/console/warehouse/WarehouseServiceDetailPage';
import DbtProjectDetailPage from 'pages/console/warehouse/DbtProjectDetailPage';
import ConnectionsPage from 'pages/console/connections/ConnectionsPage';
import SettingsPage from 'pages/console/settings/SettingsPage';
import AssistantPage from 'pages/console/assistant/AssistantPage';
import KnowledgePage from 'pages/console/assistant/KnowledgePage';
import AssistantAccess from 'pages/console/assistant/AssistantAccess';
import ProtectedRoute from 'components/auth/ProtectedRoute';
import ExternalRedirect from 'components/common/ExternalRedirect';
import useAuthStore from 'stores/authStore';
import { ForceLightTheme } from 'theme/ThemeModeProvider';
import { isSiteHost, isConsoleHost, consoleUrl, siteUrl } from 'config/hosts';

/* Console paths; on the site host they redirect to the console host. */
const CONSOLE_PATHS = ['/login', '/invite', '/console'];

function Landing() {
  /* The landing is Tailwind and light-locked — out of scope for dark. */
  return (
    <ForceLightTheme>
      <LandingPage />
    </ForceLightTheme>
  );
}

/* Console-host root and fallback: the dashboard when signed in, the landing on
   the main domain otherwise. */
function ConsoleHome() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  if (isAuthenticated) return <Navigate to="/console/dashboard" replace />;
  return <ExternalRedirect to={siteUrl('/')} />;
}

/* Site-host console path: the same path on the console host. */
function ConsoleHostRedirect() {
  const { pathname, search, hash } = useLocation();
  return <ExternalRedirect to={consoleUrl(`${pathname}${search}${hash}`)} />;
}

function SiteRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      {CONSOLE_PATHS.map((path) => (
        <Route key={path} path={`${path}/*`} element={<ConsoleHostRedirect />} />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  if (isSiteHost()) return <SiteRoutes />;
  const split = isConsoleHost();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={split ? <ConsoleHome /> : <Landing />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite/accept" element={<AcceptInvitePage />} />

      {/* Protected console routes */}
      <Route
        path="/console"
        element={
          <ProtectedRoute>
            <ConsolePage />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/console/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        {/* Report catalog */}
        <Route path="reports" element={<ReportsPage />} />
        {/* Connectors */}
        <Route path="connectors" element={<ConnectorsPage />} />
        <Route path="connectors/new" element={<NewConnectionPage />} />
        <Route path="connectors/new/files" element={<FileSourcesWizardPage />} />
        <Route path="connectors/new/bigquery" element={<BigQuerySourceWizardPage />} />
        <Route path="connectors/new/appmetrica" element={<AppMetricaSourceWizardPage />} />
        <Route path="connectors/new/direct" element={<DirectSourceWizardPage />} />
        <Route path="connectors/accounts" element={<ConnectedAccountsPage />} />
        <Route path="connectors/files/:connectionId/edit" element={<FileSourcesWizardPage />} />
        <Route
          path="connectors/bigquery/:connectionId/edit"
          element={<BigQuerySourceWizardPage />}
        />
        <Route
          path="connectors/appmetrica/:connectionId/edit"
          element={<AppMetricaSourceWizardPage />}
        />
        <Route path="connectors/direct/:connectionId/edit" element={<DirectSourceWizardPage />} />
        <Route path="connectors/:id" element={<ConnectionDetailPage />} />
        {/* Pipelines */}
        <Route path="pipelines" element={<PipelinesPage />} />
        <Route path="pipelines/new" element={<NewPipelinePage />} />
        <Route path="pipelines/storage/new" element={<NewWarehouseServicePage />} />
        <Route path="pipelines/storage/:id" element={<WarehouseServiceDetailPage />} />
        <Route path="pipelines/preparation/:id" element={<DbtProjectDetailPage />} />
        <Route path="pipelines/:id" element={<PipelineDetailPage />} />
        {/* Preserve bookmarks while storage and preparation move into loads. */}
        <Route path="warehouse" element={<LegacyWarehouseRedirect type="index" />} />
        <Route path="warehouse/new-service" element={<LegacyWarehouseRedirect type="new" />} />
        <Route path="warehouse/services/:id" element={<LegacyWarehouseRedirect type="storage" />} />
        <Route path="warehouse/projects/:id" element={<LegacyWarehouseRedirect type="preparation" />} />
        {/* Existing BI API links lead to the unified connections workspace. */}
        <Route path="bi-api" element={<Navigate to="/console/connections" replace />} />
        {/* Program setup for SQL principals and Cube/MCP API keys. */}
        <Route path="connections" element={<ConnectionsPage />} />
        {/* Discovery gates both pages before any history or knowledge fetch. */}
        <Route path="assistant" element={<AssistantAccess />}>
          <Route index element={<AssistantPage />} />
          <Route path="knowledge" element={<KnowledgePage />} />
        </Route>
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={split ? <ConsoleHome /> : <Navigate to="/" replace />} />
    </Routes>
  );
}
